// Application State
const appState = {
    currentTab: 'browse',
    currentView: 'grid',
    selectedCategory: 'all',
    searchQuery: '',
    sortBy: 'antiInflammatory',
    myStack: [],
    expandedIntervention: null
};

// Load state from localStorage
function loadState() {
    const savedStack = localStorage.getItem('lifestyleStack');
    if (savedStack) {
        appState.myStack = JSON.parse(savedStack);
        updateStackCount();
    }
}

// Save state to localStorage
function saveState() {
    localStorage.setItem('lifestyleStack', JSON.stringify(appState.myStack));
}

// Initialize the application
document.addEventListener('DOMContentLoaded', () => {
    loadState();
    initializeEventListeners();
    renderInterventions();
    renderTemplates();
    updateStackUI();
    
    // Ensure icons are created after initial render
    setTimeout(() => {
        lucide.createIcons();
    }, 100);
});

// Event Listeners
function initializeEventListeners() {
    // Tab navigation
    document.querySelectorAll('.tab-button').forEach(button => {
        button.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const tab = button.dataset.tab;
            switchTab(tab);
            // Force immediate repaint
            window.requestAnimationFrame(() => {
                void button.offsetHeight;
            });
        });
    });

    // Search input
    const searchInput = document.getElementById('search-input');
    searchInput.addEventListener('input', (e) => {
        appState.searchQuery = e.target.value.toLowerCase();
        renderInterventions();
    });

    // Sort select
    const sortSelect = document.getElementById('sort-select');
    sortSelect.addEventListener('change', (e) => {
        appState.sortBy = e.target.value;
        renderInterventions();
    });

    // View toggle
    document.querySelectorAll('.view-button').forEach(button => {
        button.addEventListener('click', () => {
            const view = button.dataset.view;
            switchView(view);
        });
    });

    // Category filters
    document.querySelectorAll('.category-chip').forEach(chip => {
        chip.addEventListener('click', () => {
            const category = chip.dataset.category;
            selectCategory(category);
        });
    });

    // Clear stack button
    document.querySelector('.clear-stack-btn')?.addEventListener('click', clearStack);

    // Export stack button
    document.querySelector('.export-stack-btn')?.addEventListener('click', exportStack);

    // Modal close
    document.querySelector('.modal-close')?.addEventListener('click', closeModal);
    document.getElementById('intervention-modal')?.addEventListener('click', (e) => {
        if (e.target.id === 'intervention-modal') {
            closeModal();
        }
    });
}

// Tab switching
function switchTab(tab) {
    appState.currentTab = tab;
    
    // Force immediate repaint by reading offsetHeight
    document.querySelectorAll('.tab-button').forEach(button => {
        if (button.dataset.tab === tab) {
            button.classList.remove('active');
            void button.offsetHeight; // Force reflow
            button.classList.add('active');
            button.style.borderBottom = '3px solid rgb(4, 61, 85)';
        } else {
            button.classList.remove('active');
            button.style.borderBottom = '';
        }
    });
    
    // Update tab panels immediately
    document.querySelectorAll('.tab-panel').forEach(panel => {
        if (panel.id === `${tab}-tab`) {
            panel.classList.add('active');
        } else {
            panel.classList.remove('active');
        }
    });
    
    // Render content synchronously to avoid delays
    if (tab === 'browse') {
        renderInterventions();
    } else if (tab === 'templates') {
        renderTemplates();
    } else if (tab === 'mystack') {
        updateStackUI();
    }
}

// View switching
function switchView(view) {
    appState.currentView = view;
    
    // Update view buttons
    document.querySelectorAll('.view-button').forEach(button => {
        button.classList.toggle('active', button.dataset.view === view);
    });
    
    // Re-render interventions with new view
    renderInterventions();
}

// Category selection
function selectCategory(category) {
    appState.selectedCategory = category;
    
    // Update category chips
    document.querySelectorAll('.category-chip').forEach(chip => {
        chip.classList.toggle('active', chip.dataset.category === category);
    });
    
    // Re-render interventions
    renderInterventions();
}

// Render interventions
function renderInterventions() {
    const container = document.getElementById('interventions-container');
    if (!container) {
        console.error('Interventions container not found');
        return;
    }
    container.innerHTML = '';
    
    // Check if interventions is defined
    if (typeof interventions === 'undefined' || !Array.isArray(interventions)) {
        console.error('Interventions data not loaded');
        container.innerHTML = '<p>Loading interventions...</p>';
        return;
    }
    
    // Filter interventions
    let filteredInterventions = interventions.filter(intervention => {
        const matchesCategory = appState.selectedCategory === 'all' || intervention.category === appState.selectedCategory;
        const matchesSearch = intervention.name.toLowerCase().includes(appState.searchQuery) ||
                            intervention.description.toLowerCase().includes(appState.searchQuery);
        return matchesCategory && matchesSearch;
    });
    
    // Sort interventions
    filteredInterventions.sort((a, b) => {
        switch (appState.sortBy) {
            case 'name':
                return a.name.localeCompare(b.name);
            case 'evidence':
                return (b.evidence?.score || 0) - (a.evidence?.score || 0);
            case 'antiInflammatory':
            default:
                return (b.antiInflammatoryScore || 0) - (a.antiInflammatoryScore || 0);
        }
    });
    
    // Update container class based on view
    container.className = appState.currentView === 'grid' ? 'interventions-grid' : 'interventions-list';
    
    // Render each intervention
    filteredInterventions.forEach((intervention, index) => {
        const element = createInterventionElement(intervention);
        container.appendChild(element);
    });
    
    // Add a single banner after 6 items or at the end if fewer than 6
    if (filteredInterventions.length >= 6) {
        // Insert banner after the 6th item
        const sixthItem = container.children[5];
        if (sixthItem) {
            const banner = createProductBanner();
            sixthItem.insertAdjacentElement('afterend', banner);
        }
    } else if (filteredInterventions.length > 0) {
        // If less than 6 items, add banner at the end
        const banner = createProductBanner();
        container.appendChild(banner);
    }
    
    // Update icons
    lucide.createIcons();
}

// Create product banner
function createProductBanner() {
    const banner = document.createElement('a');
    banner.href = 'https://corhealth.com/#product';
    banner.className = 'product-banner';
    banner.style.gridColumn = '1 / -1'; // Span full width in grid
    banner.innerHTML = `
        <div class="product-banner-content">
            <div class="product-banner-text">
                Get the COR One™ and get clear, actionable data about your inflammation
            </div>
            <div class="product-banner-arrow">
                <i data-lucide="arrow-right"></i>
            </div>
        </div>
    `;
    return banner;
}

// Create intervention element
function createInterventionElement(intervention) {
    const template = appState.currentView === 'grid' 
        ? document.getElementById('intervention-card-template')
        : document.getElementById('intervention-list-item-template');
    
    const clone = template.content.cloneNode(true);
    const element = clone.querySelector('.intervention-card, .intervention-list-item');
    
    element.dataset.interventionId = intervention.id;
    element.querySelector('.intervention-name').textContent = intervention.name;
    element.querySelector('.intervention-description').textContent = intervention.description;
    // Category is stored but not displayed - users already know from filters
    // element.querySelector('.intervention-category').textContent = intervention.category;
    element.querySelector('.intervention-duration').textContent = intervention.duration || '';
    element.querySelector('.intervention-frequency').textContent = intervention.frequency || '';
    
    // Handle scoring
    let evidenceScore = typeof intervention.evidence?.score === 'number' 
        ? intervention.evidence.score 
        : 0;
    
    // Convert to 0-10 scale if needed
    if (evidenceScore > 10) {
        evidenceScore = evidenceScore / 10;
    }
    
    let antiInflamScore = intervention.antiInflammatoryScore || 0;
    
    // Convert to 0-10 scale if needed
    if (antiInflamScore > 10) {
        antiInflamScore = antiInflamScore / 10;
    }
    
    element.querySelector('.evidence-badge').textContent = `Evidence: ${evidenceScore.toFixed(1)}/10`;
    element.querySelector('.anti-inflammatory-badge').textContent = `Anti-Inflammatory: ${antiInflamScore.toFixed(1)}/10`;
    
    // Check if already in stack
    const inStack = appState.myStack.some(item => item.intervention?.id === intervention.id);
    const addBtn = element.querySelector('.add-to-stack-btn');
    
    if (inStack) {
        addBtn.innerHTML = '<i data-lucide="check"></i> In Protocol';
        addBtn.classList.add('in-stack');
    }
    
    // Add event listeners - handle both grid and list views
    const detailsBtn = element.querySelector('.view-details-btn');
    if (detailsBtn) {
        detailsBtn.addEventListener('click', () => showInterventionDetails(intervention));
    }
    
    // Add click handler for add to stack button
    if (addBtn) {
        addBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('Add to stack clicked for:', intervention.name);
            if (inStack) {
                removeFromStack(intervention.id);
            } else {
                addToStack(intervention);
            }
        });
    }
    
    return element;
}

// Show intervention details modal
function showInterventionDetails(intervention) {
    const modal = document.getElementById('intervention-modal');
    const modalBody = modal.querySelector('.modal-body');
    const inStack = appState.myStack.some(item => item.intervention?.id === intervention.id);
    
    modalBody.innerHTML = `
        <div class="modal-header">
            <h2>${intervention.name}</h2>
            <div class="modal-badges">
                <span class="evidence-badge">Evidence: ${(intervention.evidence?.score || 0).toFixed(1)}/10 (${intervention.evidence?.studies || 0} studies)</span>
                <span class="anti-inflammatory-badge">Anti-Inflammatory: ${(intervention.antiInflammatoryScore || 0).toFixed(1)}/10</span>
            </div>
        </div>
        
        <div class="modal-section">
            <h3>Description</h3>
            <p>${intervention.description}</p>
        </div>
        
        <div class="modal-section">
            <h3>Protocol Information</h3>
            <p><strong>Duration:</strong> ${intervention.duration || 'N/A'}</p>
            <p><strong>Frequency:</strong> ${intervention.frequency || 'As needed'}</p>
            ${intervention.intensity ? `<p><strong>Intensity:</strong> ${intervention.intensity}</p>` : ''}
            ${intervention.requirements ? `<p><strong>Requirements:</strong> ${intervention.requirements}</p>` : ''}
        </div>
        
        ${intervention.keyBenefits && intervention.keyBenefits.length > 0 ? `
            <div class="modal-section">
                <h3>Key Benefits</h3>
                <ul>
                    ${intervention.keyBenefits.map(benefit => `<li>${benefit}</li>`).join('')}
                </ul>
            </div>
        ` : ''}
        
        ${intervention.mechanisms && intervention.mechanisms.length > 0 ? `
            <div class="modal-section">
                <h3>Mechanisms of Action</h3>
                <ul>
                    ${intervention.mechanisms.map(mechanism => `<li>${mechanism}</li>`).join('')}
                </ul>
            </div>
        ` : ''}
        
        ${intervention.contraindications && intervention.contraindications !== '' ? `
            <div class="modal-section">
                <h3>Contraindications</h3>
                <p>${intervention.contraindications}</p>
            </div>
        ` : ''}
        
        ${intervention.evidenceDetails ? `
            <div class="modal-section">
                <h3>Evidence Details</h3>
                <div class="evidence-breakdown">
                    <p><strong>Study Portfolio:</strong><br>
                    ${intervention.evidenceDetails.studies || 0} total studies<br>
                    ${intervention.evidenceDetails.metaAnalyses || 0} meta-analyses<br>
                    ${intervention.evidenceDetails.rcts || 0} RCTs<br>
                    Population size: ${intervention.evidenceDetails.populationSize || 'N/A'}</p>
                    
                    ${intervention.evidenceDetails.keyFindings ? `
                        <p><strong>Key Findings:</strong><br>
                        ${intervention.evidenceDetails.keyFindings}</p>
                    ` : ''}
                </div>
            </div>
        ` : ''}
        
        <div class="modal-section">
            <button class="btn-primary ${inStack ? 'in-stack' : ''}" onclick="toggleStackItem(interventions.find(i => i.id === '${intervention.id}'))">
                <i data-lucide="${inStack ? 'check' : 'plus'}"></i>
                ${inStack ? 'In Protocol' : 'Add to Protocol'}
            </button>
        </div>
    `;
    
    modal.classList.remove('hidden');
    lucide.createIcons();
}

// Close modal
function closeModal() {
    document.getElementById('intervention-modal').classList.add('hidden');
}

// Toggle stack item
function toggleStackItem(intervention) {
    const inStack = appState.myStack.some(item => item.intervention?.id === intervention.id);
    
    if (inStack) {
        removeFromStack(intervention.id);
    } else {
        addToStack(intervention);
    }
}

// Add to stack
function addToStack(intervention) {
    // Check if already in stack
    if (appState.myStack.some(item => item.intervention?.id === intervention.id)) {
        return;
    }
    
    // Add to stack
    appState.myStack.push({
        intervention: intervention,
        multiplier: 1
    });
    
    // Update UI
    updateStackCount();
    updateStackUI();
    renderInterventions(); // Re-render to update button states
    saveState();
    
    // Close modal if open
    closeModal();
    
    // Show success message
    showNotification(`${intervention.name} added to your protocol!`);
}

// Remove from stack
function removeFromStack(interventionId) {
    const intervention = appState.myStack.find(item => item.intervention?.id === interventionId)?.intervention;
    appState.myStack = appState.myStack.filter(item => item.intervention?.id !== interventionId);
    updateStackCount();
    updateStackUI();
    renderInterventions(); // Re-render to update button states
    saveState();
    
    if (intervention) {
        showNotification(`${intervention.name} removed from your protocol`);
    }
}

// Update stack count
function updateStackCount() {
    const countElement = document.querySelector('.stack-count');
    if (countElement) {
        countElement.textContent = `(${appState.myStack.length})`;
    }
}

// Update stack UI
function updateStackUI() {
    const container = document.getElementById('stack-items');
    if (!container) return;
    
    container.innerHTML = '';
    
    if (appState.myStack.length === 0) {
        container.innerHTML = `
            <div class="empty-state">
                <i data-lucide="heart"></i>
                <p>Your protocol is empty. Add interventions to get started!</p>
            </div>
        `;
    } else {
        appState.myStack.forEach(item => {
            const element = createStackItemElement(item);
            container.appendChild(element);
        });
    }
    
    // Update synergies
    updateSynergies();
    
    // Update summary
    updateStackSummary();
    
    // Add banner at the bottom of My Stack page
    const stackContainer = document.querySelector('.stack-container');
    if (stackContainer) {
        // Remove any existing banner first
        const existingBanner = stackContainer.querySelector('.product-banner');
        if (existingBanner) {
            existingBanner.remove();
        }
        // Add new banner
        const banner = createProductBanner();
        banner.style.marginTop = '3rem';
        stackContainer.appendChild(banner);
    }
    
    // Update icons
    lucide.createIcons();
}

// Create stack item element
function createStackItemElement(item) {
    const template = document.getElementById('stack-item-template');
    const clone = template.content.cloneNode(true);
    const element = clone.querySelector('.stack-item');
    
    element.dataset.interventionId = item.intervention.id;
    element.querySelector('.stack-item-name').textContent = item.intervention.name;
    
    // Build details string
    let details = [];
    if (item.intervention.duration) details.push(item.intervention.duration);
    if (item.intervention.frequency) details.push(item.intervention.frequency);
    element.querySelector('.stack-item-details').textContent = details.join(' • ');
    
    // Remove button handler
    element.querySelector('.remove-from-stack-btn').addEventListener('click', () => {
        removeFromStack(item.intervention.id);
    });
    
    return element;
}

// Update synergies
function updateSynergies() {
    const panel = document.getElementById('synergies-panel');
    const list = document.getElementById('synergies-list');
    if (!panel || !list) return;
    
    list.innerHTML = '';
    
    // Check if synergies is defined
    if (typeof synergies === 'undefined' || !Array.isArray(synergies)) {
        panel.classList.add('hidden');
        return;
    }
    
    const stackIds = appState.myStack.map(item => item.intervention.id);
    const relevantSynergies = synergies.filter(synergy => {
        const [int1, int2] = synergy.interventions || [];
        return stackIds.includes(int1) && stackIds.includes(int2);
    });
    
    if (relevantSynergies.length === 0) {
        panel.classList.add('hidden');
    } else {
        panel.classList.remove('hidden');
        
        relevantSynergies.forEach(synergy => {
            const item = document.createElement('div');
            item.className = 'synergy-item';
            
            const [int1Id, int2Id] = synergy.interventions;
            const int1 = interventions.find(i => i.id === int1Id);
            const int2 = interventions.find(i => i.id === int2Id);
            
            item.innerHTML = `
                <span class="synergy-type ${synergy.type}">${synergy.type.toUpperCase()}</span>
                <strong>${int1.name} + ${int2.name}:</strong> ${synergy.description}
            `;
            
            list.appendChild(item);
        });
    }
}

// Update stack summary
function updateStackSummary() {
    // Summary for lifestyle interventions (different from supplements/foods)
    // Could show total time commitment, etc.
}

// Clear stack
function clearStack() {
    if (confirm('Are you sure you want to clear your entire protocol?')) {
        appState.myStack = [];
        updateStackCount();
        updateStackUI();
        renderInterventions();
        saveState();
        showNotification('Protocol cleared!');
    }
}

// Export stack
function exportStack() {
    if (appState.myStack.length === 0) {
        alert('Your protocol is empty!');
        return;
    }
    
    let exportText = 'MY ANTI-INFLAMMATORY LIFESTYLE PROTOCOL\n';
    exportText += '=====================================\n\n';
    
    // Group by category
    const categories = {};
    appState.myStack.forEach(item => {
        const category = item.intervention.category;
        if (!categories[category]) {
            categories[category] = [];
        }
        categories[category].push(item.intervention);
    });
    
    // Export by category
    Object.keys(categories).forEach(category => {
        exportText += `\n${category.toUpperCase()}\n`;
        exportText += '-'.repeat(category.length) + '\n';
        
        categories[category].forEach(intervention => {
            exportText += `\n${intervention.name}\n`;
            if (intervention.duration) exportText += `Duration: ${intervention.duration}\n`;
            if (intervention.frequency) exportText += `Frequency: ${intervention.frequency}\n`;
            if (intervention.intensity) exportText += `Intensity: ${intervention.intensity}\n`;
            if (intervention.requirements) exportText += `Requirements: ${intervention.requirements}\n`;
            exportText += `Evidence Score: ${(intervention.evidence?.score || 0).toFixed(1)}/10\n`;
            exportText += `Anti-Inflammatory Score: ${(intervention.antiInflammatoryScore || 0).toFixed(1)}/10\n`;
        });
    });
    
    // Add synergies if present
    if (typeof synergies !== 'undefined' && Array.isArray(synergies)) {
        const stackIds = appState.myStack.map(item => item.intervention.id);
        const relevantSynergies = synergies.filter(synergy => {
            const [int1, int2] = synergy.interventions || [];
            return stackIds.includes(int1) && stackIds.includes(int2);
        });
    
        if (relevantSynergies.length > 0) {
            exportText += '\n\nPROTOCOL SYNERGIES:\n';
            relevantSynergies.forEach(synergy => {
                const [int1Id, int2Id] = synergy.interventions;
                const int1 = interventions.find(i => i.id === int1Id);
                const int2 = interventions.find(i => i.id === int2Id);
                if (int1 && int2) {
                    exportText += `- ${int1.name} + ${int2.name}: ${synergy.description}\n`;
                }
            });
        }
    }
    
    // Create download
    const blob = new Blob([exportText], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'my-anti-inflammatory-protocol.txt';
    a.click();
    window.URL.revokeObjectURL(url);
    
    showNotification('Protocol exported successfully!');
}

// Render templates
function renderTemplates() {
    const container = document.querySelector('.templates-grid');
    if (!container) return;
    
    container.innerHTML = '';
    
    // Check if lifestyleProtocols is defined
    if (typeof lifestyleProtocols === 'undefined' || !Array.isArray(lifestyleProtocols)) {
        container.innerHTML = '<p>No protocol templates available</p>';
        return;
    }
    
    lifestyleProtocols.forEach(template => {
        const element = createTemplateElement(template);
        container.appendChild(element);
    });
    
    // Add banner at the end
    const banner = createProductBanner();
    banner.style.marginTop = '3rem';
    container.appendChild(banner);
    
    lucide.createIcons();
}

// Create template element
function createTemplateElement(template) {
    const div = document.createElement('div');
    div.className = 'template-card';
    
    div.innerHTML = `
        <div class="template-icon">
            <i data-lucide="${template.icon}"></i>
        </div>
        <h3 class="template-name">${template.name}</h3>
        <p class="template-description">${template.description}</p>
        <p class="template-count">${template.interventions.length} interventions</p>
    `;
    
    div.addEventListener('click', () => applyTemplate(template));
    
    return div;
}

// Apply template
function applyTemplate(template) {
    if (appState.myStack.length > 0) {
        if (!confirm('This will replace your current protocol. Continue?')) {
            return;
        }
    }
    
    appState.myStack = [];
    
    template.interventions.forEach(interventionId => {
        const intervention = interventions.find(i => i.id === interventionId);
        if (intervention) {
            appState.myStack.push({
                intervention: intervention,
                multiplier: 1
            });
        }
    });
    
    updateStackCount();
    updateStackUI();
    renderInterventions();
    saveState();
    
    // Switch to My Stack tab
    switchTab('mystack');
    
    showNotification(`Applied "${template.name}" protocol!`);
}

// Show notification
function showNotification(message) {
    // Create notification element
    const notification = document.createElement('div');
    notification.style.cssText = `
        position: fixed;
        bottom: 20px;
        right: 20px;
        background-color: #2ecc71;
        color: white;
        padding: 15px 20px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        z-index: 1001;
        animation: slideIn 0.3s ease-out;
    `;
    notification.textContent = message;
    
    // Add animation
    const style = document.createElement('style');
    style.textContent = `
        @keyframes slideIn {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
        }
    `;
    document.head.appendChild(style);
    
    document.body.appendChild(notification);
    
    // Remove after 3 seconds
    setTimeout(() => {
        notification.style.animation = 'slideOut 0.3s ease-out';
        notification.style.animationFillMode = 'forwards';
        
        setTimeout(() => {
            notification.remove();
            style.remove();
        }, 300);
    }, 3000);
}